import type { LngLat } from './types.ts';
import { API_BASE } from './api.ts';
import { distanceM } from './geo.ts';

export interface DirectoryCourse {
  id: string;
  name: string;
  lat: number;
  lng: number;
  state: string;
  holes: number | null;
  /** Total par when OSM has it. Australian courses tag par far more often
   *  than hole count, and neither is ever inferred from the other. */
  par?: number | null;
  website?: string;
  phone?: string;
  suburb?: string;
  access?: string;
}

export interface Directory {
  source: string;
  harvestedAt: number;
  count: number;
  byState: Record<string, number>;
  courses: DirectoryCourse[];
}

/**
 * The national course directory ships inside the app rather than being fetched.
 *
 * Reasons, in order of importance:
 *   1. a buggy in a valley with no signal must still be able to find a course
 *   2. 1000+ courses is roughly 150 KB of JSON — smaller than one map tile
 *   3. no per-device API calls means no per-device cost at fleet scale
 *
 * Refreshed by scripts/MERCURY_FAIRWAY_HARVEST_COURSES.py.
 */
let cache: Directory | null = null;

export async function loadDirectory(): Promise<Directory> {
  if (cache) return cache;
  // Code-split: the directory is only pulled in when the browser screen opens,
  // so it never slows the first paint of the map on a cold head unit.
  const mod = await import('../data/au-courses.json');
  cache = (mod.default ?? mod) as unknown as Directory;
  return cache;
}

export const STATE_NAMES: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'ACT',
};

/** Fold accents and punctuation so "Coolangatta-Tweed" matches "coolangatta tweed". */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export interface SearchOpts {
  query?: string;
  state?: string;
  near?: LngLat | null;
  limit?: number;
  /**
   * Distance is the right order for "what is near me", and the wrong one the
   * moment somebody starts typing: a searcher is looking for a NAME, and a
   * list that reshuffles by proximity as they type is one they cannot predict
   * or scan. Defaults to distance only when no query is involved.
   */
  sort?: 'distance' | 'name';
}

export interface SearchHit extends DirectoryCourse {
  distanceM: number | null;
}

/**
 * Search 1000+ courses. Deliberately plain string matching rather than a fuzzy
 * index: a golfer types two or three letters of a name they already know, and a
 * head unit CPU should not be building a trigram index to answer that.
 */
export function searchDirectory(dir: Directory, opts: SearchOpts): SearchHit[] {
  const { query = '', state = '', near = null, limit = 200, sort = near ? 'distance' : 'name' } = opts;
  const q = norm(query);
  const terms = q ? q.split(' ').filter(Boolean) : [];

  const hits: SearchHit[] = [];
  for (const c of dir.courses) {
    if (state && c.state !== state) continue;
    if (terms.length) {
      const hay = norm(`${c.name} ${c.suburb ?? ''} ${c.state}`);
      if (!terms.every((t) => hay.includes(t))) continue;
    }
    hits.push({ ...c, distanceM: near ? distanceM(near, [c.lng, c.lat]) : null });
  }

  hits.sort((a, b) => {
    if (sort === 'distance' && near) return (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity);
    // Without a position, exact prefix matches beat mid-string matches.
    if (terms.length) {
      const ap = norm(a.name).startsWith(terms[0]) ? 0 : 1;
      const bp = norm(b.name).startsWith(terms[0]) ? 0 : 1;
      if (ap !== bp) return ap - bp;
    }
    return a.name.localeCompare(b.name);
  });

  return hits.slice(0, limit);
}

export function nearestCourses(dir: Directory, at: LngLat, n = 5): SearchHit[] {
  return searchDirectory(dir, { near: at, limit: n });
}

export function formatKm(metres: number | null): string {
  if (metres == null) return '';
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

/**
 * Courses near a position, from the server, for anywhere the bundled directory
 * does not cover.
 *
 * The directory in the app is Australian — 1,449 courses, offline, instant.
 * Outside it a golfer got a working road map and no hole, no yardage and no
 * dashboard tile, with nothing on screen explaining why. This asks the server,
 * which asks OpenStreetMap, and the IDs it returns are the same ones the course
 * builder already understands.
 *
 * Deliberately not merged into the bundled search: this costs a round trip and
 * the local list must stay instant.
 */
export async function nearbyCourses(at: LngLat, radiusM = 6000): Promise<DirectoryCourse[]> {
  try {
    // API_BASE, not a bare path — see places.ts.
    const r = await fetch(`${API_BASE}/api/nearby?lat=${at[1]}&lng=${at[0]}&radius=${radiusM}`);
    if (!r.ok) return [];
    const rows = (await r.json()) as DirectoryCourse[];
    return rows
      .map((c) => ({ ...c, suburb: c.suburb ?? '' }))
      .sort((a, b) => distanceM(at, [a.lng, a.lat]) - distanceM(at, [b.lng, b.lat]));
  } catch {
    return [];
  }
}
